import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("atf_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("atf_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip if returning from Google OAuth callback (handled by AuthCallback)
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    loadUser();
  }, [loadUser]);

  const setSession = (token, userData) => {
    localStorage.setItem("atf_token", token);
    setUser(userData);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setSession(data.token, data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setSession(data.token, data.user);
    return data.user;
  };

  const logout = async () => {
    // Clear the credential and in-memory identity FIRST so no in-flight request can
    // keep using the previous user's token, then best-effort notify the backend.
    localStorage.removeItem("atf_token");
    setUser(false);
    try { await api.post("/auth/logout"); } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setSession, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
